import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ChevronRight, Truck, RotateCcw, Shield, Minus, Plus, Check, Package, Clock, Star, Ruler } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import ProductCard from '../components/ProductCard';
import ReviewSection from '../components/ReviewSection';
import SizeGuide from '../components/SizeGuide';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductPage = () => {
  const { slug } = useParams();
  const { addToCart } = useCart();
  const { isAuthenticated } = useAuth();
  const [product, setProduct] = useState(null);
  const [recommendedProducts, setRecommendedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [adding, setAdding] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  const [showSizeGuide, setShowSizeGuide] = useState(false);

  const getBadgeInfo = (badge) => {
    switch(badge) {
      case 'bestseller': return { text: 'BEST SELLER', color: 'bg-[#c00000] text-white' };
      case 'popular': return { text: 'MODEL POPULAR', color: 'bg-white text-black' };
      case 'limited': return { text: 'STOC LIMITAT', color: 'bg-yellow-500 text-black' };
      case 'new': return { text: 'NOU', color: 'bg-green-500 text-white' };
      default: return null;
    }
  };

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/products/${slug}`);
        setProduct(res.data);
        setSelectedSize(null);
        setQuantity(1);
        setSelectedImage(0);
        
        // Fetch admin-selected recommended products
        const recommendedRes = await axios.get(`${API}/products/recommended/list`);
        // Filter out the current product from recommendations
        setRecommendedProducts(recommendedRes.data.filter(p => p.id !== res.data.id).slice(0, 4));
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [slug]);

  const handleAddToCart = async () => {
    if (!selectedSize) {
      toast.error('Te rugăm să selectezi o mărime');
      return;
    }
    
    setAdding(true);
    const success = await addToCart(product.id, selectedSize, quantity);
    setAdding(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="animate-pulse text-white/50">Se încarcă...</div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex flex-col items-center justify-center">
        <h1 className="font-heading text-3xl text-white mb-4">Produs Negăsit</h1>
        <Link to="/shop">
          <Button className="bg-[#c00000] text-white rounded-none">Înapoi la Magazin</Button>
        </Link>
      </div>
    );
  }

  // Generate SEO keywords based on product
  const getCategoryKeywords = (category) => {
    if (category?.includes('yeezy-350')) return 'Yeezy 350 V2, Adidas Yeezy, Yeezy Romania';
    if (category?.includes('yeezy')) return 'Adidas Yeezy, Yeezy Romania';
    if (category?.includes('louboutin')) return 'Louboutin, Christian Louboutin Romania';
    if (category?.includes('mcqueen')) return 'Alexander McQueen, McQueen Sneakers Romania';
    return 'Sneakers Premium Romania';
  };

  // Product Schema for Google
  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.name,
    "description": product.description || `${product.name} - Sneakers premium disponibil la DEPRO KICKS Romania. Livrare rapidă, retur garantat.`,
    "image": product.images?.[0] || '',
    "brand": {
      "@type": "Brand",
      "name": product.category?.includes('yeezy') ? "Adidas Yeezy" : 
              product.category?.includes('louboutin') ? "Christian Louboutin" :
              product.category?.includes('mcqueen') ? "Alexander McQueen" : "DEPRO KICKS"
    },
    "sku": product.id,
    "offers": {
      "@type": "Offer",
      "url": `https://deprokicks.com/product/${product.slug}`,
      "priceCurrency": "RON",
      "price": product.price,
      "priceValidUntil": new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      "availability": product.in_stock !== false ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      "seller": {
        "@type": "Organization",
        "name": "DEPRO KICKS"
      }
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "150"
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Dynamic SEO Meta Tags */}
      <Helmet>
        <title>{`${product.name} | DEPRO KICKS – Yeezy Romania`}</title>
        <meta name="description" content={`Cumpără ${product.name} de la DEPRO KICKS. ${getCategoryKeywords(product.category)}. Preț: ${product.price} RON. Livrare rapidă în România, retur garantat 14 zile.`} />
        <meta name="keywords" content={`${product.name}, ${getCategoryKeywords(product.category)}, sneakers romania, depro kicks`} />
        <meta property="og:title" content={`${product.name} | DEPRO KICKS`} />
        <meta property="og:description" content={`${product.name} - ${product.price} RON. Sneakers premium la DEPRO KICKS Romania.`} />
        <meta property="og:image" content={product.images?.[0] || ''} />
        <meta property="og:url" content={`https://deprokicks.com/product/${product.slug}`} />
        <link rel="canonical" href={`https://deprokicks.com/product/${product.slug}`} />
        <script type="application/ld+json">{JSON.stringify(productSchema)}</script>
      </Helmet>

      {/* Breadcrumb */}
      <div className="px-6 md:px-12 lg:px-24 py-6 border-b border-white/5">
        <div className="flex items-center gap-2 font-body text-sm text-white/50">
          <Link to="/" className="hover:text-white transition-colors">Acasă</Link>
          <ChevronRight className="w-4 h-4" />
          <Link to="/shop" className="hover:text-white transition-colors">Magazin</Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-white">{product.name}</span>
        </div>
      </div>

      {/* Product */}
      <div className="px-6 md:px-12 lg:px-24 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24">
          {/* Image Gallery */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="relative"
          >
            <div className="sticky top-28 space-y-4">
              {/* Main Image */}
              <div className="aspect-square bg-[#121212] overflow-hidden relative">
                <img
                  src={product.images[selectedImage]}
                  alt={product.name}
                  className="w-full h-full object-cover transition-opacity duration-300"
                  data-testid="product-image"
                />
                {product.is_reflective && (
                  <span className="absolute top-4 left-4 bg-[#c00000] text-white text-xs font-heading tracking-wider px-3 py-1">
                    REFLECTIVE
                  </span>
                )}
              </div>
              
              {/* Thumbnail Gallery */}
              {product.images.length > 1 && (
                <div className="grid grid-cols-5 gap-2">
                  {product.images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedImage(idx)}
                      className={`aspect-square bg-[#121212] overflow-hidden border-2 transition-all ${
                        selectedImage === idx 
                          ? 'border-[#c00000]' 
                          : 'border-transparent hover:border-white/20'
                      }`}
                      data-testid={`thumbnail-${idx}`}
                    >
                      <img
                        src={img}
                        alt={`${product.name} vedere ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </motion.div>

          {/* Info */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:py-8"
          >
            {/* Badges */}
            <div className="flex flex-wrap gap-2 mb-4">
              {product.badge && getBadgeInfo(product.badge) && (
                <span className={`text-xs font-heading tracking-wider px-3 py-1 ${getBadgeInfo(product.badge).color}`}>
                  {getBadgeInfo(product.badge).text}
                </span>
              )}
              {product.is_reflective && (
                <span className="bg-[#c00000] text-white text-xs font-heading tracking-wider px-3 py-1">
                  REFLECTIVE
                </span>
              )}
            </div>
            
            <span className="font-body text-xs tracking-[0.2em] text-[#c00000] uppercase">
              {product.category.replace(/-/g, ' ')}
            </span>
            <h1 className="font-heading font-bold text-4xl md:text-5xl tracking-tight uppercase text-white mt-2 mb-4" data-testid="product-title">
              {product.name}
            </h1>
            
            <div className="flex items-baseline gap-4 mb-4">
              <span className="font-heading font-bold text-3xl text-[#c00000]" data-testid="product-price">
                {product.price} RON
              </span>
              {product.original_price && (
                <span className="font-body text-lg text-white/30 line-through">
                  {product.original_price} RON
                </span>
              )}
            </div>

            {/* Quick trust indicators */}
            <div className="flex flex-wrap gap-4 mb-8 pb-6 border-b border-white/5">
              <div className="flex items-center gap-2 text-green-500 text-sm">
                <Check className="w-4 h-4" />
                <span className="font-body">În Stoc</span>
              </div>
              <div className="flex items-center gap-2 text-white/60 text-sm">
                <Truck className="w-4 h-4" />
                <span className="font-body">Livrare Gratuită</span>
              </div>
              <div className="flex items-center gap-2 text-white/60 text-sm">
                <Clock className="w-4 h-4" />
                <span className="font-body">Livrare 2-4 Zile</span>
              </div>
            </div>

            <p className="font-body text-white/60 leading-relaxed mb-8">
              {product.description}
            </p>

            {/* Size Selection */}
            <div className="mb-8">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-heading font-bold text-sm tracking-wider text-white">SELECTEAZĂ MĂRIMEA (EU)</h3>
                <button 
                  onClick={() => setShowSizeGuide(true)}
                  className="flex items-center gap-1 font-body text-xs text-[#c00000] hover:text-white transition-colors"
                  data-testid="size-guide-btn"
                >
                  <Ruler className="w-3 h-3" />
                  Ghid Mărimi
                </button>
              </div>
              <div className="grid grid-cols-6 gap-2">
                {product.sizes.map(size => (
                  <button
                    key={size}
                    onClick={() => setSelectedSize(size)}
                    className={`py-3 font-body text-sm border transition-all duration-200 ${
                      selectedSize === size
                        ? 'border-[#c00000] bg-[#c00000] text-white'
                        : 'border-white/10 text-white/60 hover:border-white/30 hover:text-white'
                    }`}
                    data-testid={`size-${size}`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>

            {/* Quantity */}
            <div className="mb-8">
              <h3 className="font-heading font-bold text-sm tracking-wider text-white mb-4">CANTITATE</h3>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-12 h-12 border border-white/10 flex items-center justify-center text-white hover:border-white/30 transition-colors"
                  data-testid="qty-minus"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="font-body text-lg text-white w-8 text-center" data-testid="qty-value">{quantity}</span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="w-12 h-12 border border-white/10 flex items-center justify-center text-white hover:border-white/30 transition-colors"
                  data-testid="qty-plus"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Add to Cart */}
            <Button
              onClick={handleAddToCart}
              disabled={adding}
              className="w-full bg-[#c00000] text-white hover:bg-[#a00000] rounded-none py-6 font-heading font-bold tracking-wider uppercase text-lg transition-all duration-300"
              data-testid="add-to-cart-btn"
            >
              {adding ? 'SE ADAUGĂ...' : 'ADAUGĂ ÎN COȘ'}
            </Button>

            {/* Trust badges */}
            <div className="mt-8 pt-8 border-t border-white/5 space-y-4">
              <div className="flex items-center gap-4 text-white/60">
                <Truck className="w-5 h-5" />
                <span className="font-body text-sm">Livrare gratuită în toată România</span>
              </div>
              <div className="flex items-center gap-4 text-white/60">
                <RotateCcw className="w-5 h-5" />
                <span className="font-body text-sm">Retur gratuit în 14 zile</span>
              </div>
              <div className="flex items-center gap-4 text-white/60">
                <Shield className="w-5 h-5" />
                <span className="font-body text-sm">Calitate premium garantată</span>
              </div>
            </div>

            {/* FAQ */}
            <div className="mt-8 pt-8 border-t border-white/5">
              <h3 className="font-heading font-bold text-sm tracking-wider text-white mb-4">ÎNTREBĂRI FRECVENTE</h3>
              <div className="space-y-4 text-sm">
                <div>
                  <p className="font-body font-semibold text-white/80">Cât durează livrarea?</p>
                  <p className="font-body text-white/50 mt-1">2-4 zile lucrătoare în toată România.</p>
                </div>
                <div>
                  <p className="font-body font-semibold text-white/80">Pot plăti la livrare?</p>
                  <p className="font-body text-white/50 mt-1">Da, plata la livrare cu posibilitatea de a verifica coletul.</p>
                </div>
                <div>
                  <p className="font-body font-semibold text-white/80">Ce fac dacă mărimea nu este potrivită?</p>
                  <p className="font-body text-white/50 mt-1">Retur gratuit în 14 zile, fără întrebări.</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Customer Reviews Section */}
      <div className="px-6 md:px-12 lg:px-24">
        <ReviewSection productId={product.id} productName={product.name} />
      </div>

      {/* Recommended Products - Admin Selected */}
      {recommendedProducts.length > 0 && (
        <div className="px-6 md:px-12 lg:px-24 py-24 border-t border-white/5">
          <h2 className="font-heading font-bold text-2xl md:text-3xl tracking-wider uppercase text-white mb-12">
            Produse Recomandate
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {recommendedProducts.map(recProduct => (
              <ProductCard key={recProduct.id} product={recProduct} />
            ))}
          </div>
        </div>
      )}

      {/* Size Guide Modal */}
      <SizeGuide 
        isOpen={showSizeGuide} 
        onClose={() => setShowSizeGuide(false)} 
        category={product?.category}
      />
    </div>
  );
};

export default ProductPage;
